import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import NumericInput from "@/components/NumericInput";
import { ArrowRightLeft, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Depolar arası transfer — atomik ($inc + $gte) + idempotent (client tarafında uuid).
export default function WarehouseTransferDialog({ open, onOpenChange, warehouses, defaultSourceId = "", onDone }) {
  const [srcId, setSrcId] = useState(defaultSourceId);
  const [tgtId, setTgtId] = useState("");
  const [note, setNote] = useState("");
  const [inv, setInv] = useState([]);   // kaynak deponun stok listesi
  const [rows, setRows] = useState([{ key: "", qty: "" }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setSrcId(defaultSourceId); setTgtId(""); setNote(""); setRows([{ key: "", qty: "" }]); } }, [open, defaultSourceId]);

  useEffect(() => {
    if (!srcId) { setInv([]); return; }
    api.get(`/warehouses/${srcId}/inventory`)
      .then((r) => setInv(r.data.rows || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [srcId]);

  const byKey = useMemo(() => Object.fromEntries(inv.map((r) => [`${r.product_id}|${r.variant_id || ""}`, r])), [inv]);

  const setRow = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, { key: "", qty: "" }]);
  const rmRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!srcId || !tgtId) return toast.error("Kaynak ve hedef depo seçin");
    if (srcId === tgtId) return toast.error("Kaynak ve hedef aynı olamaz");
    const items = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.key) return toast.error(`Satır ${i + 1}: ürün seçin`);
      const [pid, vid] = r.key.split("|");
      const qty = Number(r.qty);
      if (!qty || qty <= 0) return toast.error(`Satır ${i + 1}: geçerli miktar girin`);
      const src = byKey[r.key];
      if (src && qty > Number(src.quantity)) return toast.error(`Satır ${i + 1}: kaynak stoktan fazla (${src.quantity})`);
      items.push({ product_id: pid, variant_id: vid || "", quantity: qty });
    }
    try {
      setBusy(true);
      const transfer_id = crypto.randomUUID();
      const { data } = await api.post("/warehouses/transfers", { transfer_id, source_warehouse_id: srcId, target_warehouse_id: tgtId, note, items });
      toast.success(`Transfer #${(data.transfer_id || "").slice(0, 8)} uygulandı`);
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-3xl" data-testid="warehouse-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-4 h-4" /> Depolar Arası Transfer</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase font-mono text-slate-500">Kaynak Depo</label>
              <Select value={srcId} onValueChange={setSrcId}>
                <SelectTrigger data-testid="tr-src-select" className="bg-slate-950 border-slate-700 rounded-sm mt-1"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase font-mono text-slate-500">Hedef Depo</label>
              <Select value={tgtId} onValueChange={setTgtId}>
                <SelectTrigger data-testid="tr-tgt-select" className="bg-slate-950 border-slate-700 rounded-sm mt-1"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{warehouses.filter((w) => w.id !== srcId).map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="border border-slate-800 rounded-sm p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase font-mono text-slate-500">Satırlar</div>
              <Button type="button" size="sm" variant="ghost" onClick={addRow} className="h-6 px-2 text-xs text-blue-400" data-testid="tr-add-row"><Plus className="w-3 h-3 mr-1" /> Satır ekle</Button>
            </div>
            {rows.map((r, i) => {
              const src = r.key ? byKey[r.key] : null;
              return (
                <div key={i} className="grid grid-cols-[1fr_130px_100px_28px] gap-2 items-center" data-testid={`tr-row-${i}`}>
                  <Select value={r.key} onValueChange={(v) => setRow(i, { key: v })}>
                    <SelectTrigger data-testid={`tr-row-item-${i}`} className="bg-slate-950 border-slate-700 rounded-sm text-xs"><SelectValue placeholder="Ürün / varyant seç" /></SelectTrigger>
                    <SelectContent className="max-h-72">{inv.filter((x) => Number(x.quantity) > 0).map((x) => (
                      <SelectItem key={`${x.product_id}|${x.variant_id || ""}`} value={`${x.product_id}|${x.variant_id || ""}`}>
                        {x.product_name}{x.variant_label ? " — " + x.variant_label : ""} · {x.quantity}
                      </SelectItem>
                    ))}</SelectContent>
                  </Select>
                  <div className="text-[11px] font-mono text-slate-400">Mevcut: <span className="text-slate-100">{src ? src.quantity : "—"}</span></div>
                  <NumericInput data-testid={`tr-row-qty-${i}`} value={r.qty} onChange={(v) => setRow(i, { qty: v })} min={0} step="1" placeholder="Miktar" className="bg-slate-950 border-slate-700 rounded-sm text-sm" />
                  <Button type="button" size="sm" variant="ghost" onClick={() => rmRow(i)} disabled={rows.length === 1} className="h-8 px-1 text-slate-400 hover:text-rose-400"><X className="w-3.5 h-3.5" /></Button>
                </div>
              );
            })}
          </div>
          <div>
            <label className="text-[10px] uppercase font-mono text-slate-500">Açıklama (opsiyonel)</label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Sevkiyat, düzenleme, vs." className="bg-slate-950 border-slate-700 rounded-sm mt-1" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm text-xs border-slate-700">İptal</Button>
            <Button disabled={busy} onClick={submit} className="rounded-sm bg-emerald-600 hover:bg-emerald-500 uppercase text-xs font-semibold" data-testid="tr-submit">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />} Transferi Uygula
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
